/**
 * Single source of truth for "today" on the client.
 *
 * Always use these helpers instead of `new Date().toISOString().slice(0,10)`
 * or `.split('T')[0]` - those return UTC today, which is wrong for any user
 * not on UTC (e.g. travelers, anyone west of UTC late at night).
 */

import { format } from 'date-fns';
import { todayForUser, dateStrLocal } from '@/lib/userTime';

/** Re-exported so callers have one import for day-stamping writes. */
export { todayForUser, dateStrLocal };

/** Today as 'yyyy-MM-dd' in the device's local timezone. */
export const getTodayLocal = (): string => format(new Date(), 'yyyy-MM-dd');

/** Device timezone offset in minutes (matches Date.prototype.getTimezoneOffset). */
export const getDeviceTzOffsetMinutes = (): number => new Date().getTimezoneOffset();

/** IANA zone name when available, else null. */
export const getDeviceTimeZone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

/** Returns true if the yyyy-MM-dd string is strictly after device today. */
export const isFutureLocal = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return false;
  return dateStr > getTodayLocal();
};

/**
 * Body fragment to send to any edge function whose logic depends on the
 * caller's calendar day. Spread into your request body:
 *
 *   supabase.functions.invoke('scan-receipt', {
 *     body: { ...payload, ...clientDateContext() }
 *   });
 */
export const clientDateContext = () => ({
  clientToday: todayForUser(),
  clientTzOffsetMinutes: getDeviceTzOffsetMinutes(),
  clientTimeZone: getDeviceTimeZone(),
});
